/**
 * Legacy cache migrator for the Inspections listing.
 *
 * Converts the old Kendo localStorage shape:
 *   { page: { skip, take }, sort: [{ field, dir }], filters: { values, active, operators }, columns: string[] }
 * into a partial ListingPreferences<InspectionFilters> understood by useListingPreferences.
 *
 * Contract rules applied here (from the frozen contract):
 *  - Accept the { data: ... } envelope (handled by useListingPreferences).
 *  - Convert page.take → pageSize; page.skip + take → one-indexed page number (ignored; page number is never restored).
 *  - Convert sort[0] → { field, direction }.
 *  - Convert filters.active (boolean map) → activeFilterKeys array.
 *  - Convert old date-range objects ({ rangeType, startDate, endDate, fieldType }) → InspectionDateRange.
 *  - Convert old multi-select values (empty string / single value / array) → typed number[]/string[] arrays.
 *  - Convert old boolean string values ('true'/'false'/'') → boolean | null.
 *  - Scope-injected values (building in building scope, etc.) are stripped — scope is re-applied by the adapter.
 *  - Search is never read or written.
 *  - Invalid / unrecognizable shapes return null.
 */
import { INITIAL_INSPECTION_FILTERS, DEFAULT_ACTIVE_FILTER_KEYS } from '../types';
function isLegacyCache(raw) {
    if (!raw || typeof raw !== 'object')
        return false;
    const obj = raw;
    // At minimum we need filters.active, filters.values, filters.operators to be present
    if (!obj.filters || typeof obj.filters !== 'object')
        return false;
    const filters = obj.filters;
    const hasValidFilters = typeof filters.active === 'object' &&
        filters.active !== null &&
        typeof filters.values === 'object' &&
        filters.values !== null;
    if (!hasValidFilters)
        return false;
    if (obj.sort !== undefined && !isLegacySort(obj.sort))
        return false;
    return true;
}
function isLegacySort(value) {
    if (!Array.isArray(value))
        return false;
    if (value.length === 0)
        return true;
    const first = value[0];
    return (first !== null && typeof first === 'object' && typeof first.field === 'string' && typeof first.dir === 'string');
}
// ---------------------------------------------------------------------------
// Value converters
// ---------------------------------------------------------------------------
/** Convert a Kendo date-range state object → InspectionDateRange (preserving rangeType). */
function migrateDate(raw) {
    if (!raw || typeof raw !== 'object')
        return { start: null, end: null, rangeType: null };
    const obj = raw;
    if (obj.fieldType !== 'dateRange')
        return { start: null, end: null, rangeType: null };
    return {
        start: typeof obj.startDate === 'string' && obj.startDate ? obj.startDate : null,
        end: typeof obj.endDate === 'string' && obj.endDate ? obj.endDate : null,
        rangeType: typeof obj.rangeType === 'string' && obj.rangeType ? obj.rangeType : null
    };
}
/** Convert an old multi-select value to a typed number array. */
function migrateNumberArray(raw) {
    if (raw == null || raw === '')
        return [];
    if (Array.isArray(raw))
        return raw.map(Number).filter((n) => !Number.isNaN(n));
    const n = Number(raw);
    return Number.isNaN(n) ? [] : [n];
}
/** Convert an old multi-select value to a typed string array. */
function migrateStringArray(raw) {
    if (raw == null || raw === '')
        return [];
    if (Array.isArray(raw))
        return raw.map(String);
    return [String(raw)];
}
/** Convert old boolean-select string ('true'/'false'/'') → boolean | null. */
function migrateBooleanSelect(raw) {
    if (raw === 'true' || raw === true)
        return true;
    if (raw === 'false' || raw === false)
        return false;
    return null;
}
/** Convert old checkbox string ('true'/'') → boolean. */
function migrateCheckbox(raw) {
    return raw === 'true' || raw === true;
}
/** Convert old text value → string. */
function migrateText(raw) {
    if (typeof raw === 'string')
        return raw;
    return '';
}
// ---------------------------------------------------------------------------
// Main migrator
// ---------------------------------------------------------------------------
function migrateScopedValue(scope, field, value, emptyValue) {
    return scope.type === field ? emptyValue : value;
}
export function createInspectionsLegacyMigrator(scope) {
    return (raw) => {
        var _a, _b, _c;
        if (!isLegacyCache(raw))
            return null;
        const legacy = raw;
        // isLegacyCache ensures filters exists and has values/active objects
        const lv = legacy.filters.values;
        const la = legacy.filters.active;
        // ── Page size ──
        const pageSize = (_b = (_a = legacy.page) === null || _a === void 0 ? void 0 : _a.take) !== null && _b !== void 0 ? _b : 25;
        // ── Sort ──
        let sort = null;
        if (legacy.sort && legacy.sort.length > 0) {
            const s = legacy.sort[0];
            sort = {
                field: s.field,
                direction: s.dir === 'asc' || s.dir === 'desc' ? s.dir : 'asc'
            };
        }
        // ── Active filter keys ──
        const activeFilterKeys = Object.entries(la)
            .filter(([, active]) => active === true)
            .map(([key]) => key);
        // ── Filter values ──
        // Scope-injected values are cleared only for the matching current scope; standalone preferences keep saved filters.
        const filters = Object.assign(Object.assign({}, INITIAL_INSPECTION_FILTERS), { 
            // Date ranges (four legacy adapters — visitDate shipped after the first three)
            scheduledDate: migrateDate(lv.scheduledDate), inspectionDate: migrateDate(lv.inspectionDate), dueDate: migrateDate(lv.dueDate), visitDate: migrateDate(lv.visitDate), 
            // Multi-selects
            frequency: migrateStringArray(lv.frequency), status: migrateStringArray(lv.status), technician: migrateNumberArray(lv.technician), asset: migrateStringArray(lv.asset), technicianTeam: migrateNumberArray(lv.technicianTeam), territory: migrateNumberArray(lv.territory), additionalTerritory: migrateNumberArray(lv.additionalTerritory), tags: migrateNumberArray(lv.tags), buildingTags: migrateNumberArray(lv.buildingTags), accountTags: migrateNumberArray(lv.accountTags), workOrderTags: migrateNumberArray(lv.workOrderTags), subcontractor: migrateNumberArray(lv.subcontractor), inspectionType: migrateNumberArray(lv.inspectionType), 
            // Boolean selects
            buildingStatus: migrateBooleanSelect(lv.buildingStatus), noAccess: migrateBooleanSelect(lv.noAccess), 
            // Text fields
            zipCode: migrateText(lv.zipCode), referenceNumber: migrateText(lv.referenceNumber), workOrder: migrateText(lv.workOrder), inspectionId: migrateText(lv.inspectionId), 
            // Checkboxes
            tce: migrateCheckbox(lv.tce), tceErrors: migrateCheckbox(lv.tceErrors), irol: migrateCheckbox(lv.irol), irolErrors: migrateCheckbox(lv.irolErrors), livsafe: migrateCheckbox(lv.livsafe), livsafeErrors: migrateCheckbox(lv.livsafeErrors), building: migrateScopedValue(scope, 'building', migrateNumberArray(lv.building), []), account: migrateScopedValue(scope, 'account', migrateNumberArray(lv.account), []), inspectionSeries: migrateScopedValue(scope, 'inspectionSeries', (_c = migrateNumberArray(lv.inspectionSeries)[0]) !== null && _c !== void 0 ? _c : null, null) });
        // ── Visible columns ──
        const visibleColumnKeys = Array.isArray(legacy.columns) ? legacy.columns.filter((c) => c !== 'selected') : [];
        return { pageSize, sort, filters, activeFilterKeys, visibleColumnKeys };
    };
}
export const inspectionsLegacyMigrator = createInspectionsLegacyMigrator({
    type: 'standalone'
});
/**
 * Active filter keys used when no valid preferences exist.
 * Mirrors INITIAL_FILTERS.active from constants.js.
 */
export { DEFAULT_ACTIVE_FILTER_KEYS };
